import { useState, useEffect, useMemo, useRef } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, serverTimestamp, query, orderBy, setDoc, getDocs
} from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth, googleProvider } from "./firebase";
import { SITES, SITE_DETAILS } from "./sites";
import * as XLSX from "xlsx";

// ── VERSION ──────────────────────────────────────────────────
const APP_VERSION = "1.7.9";
const APP_VERSION_DATE = "2026-08-12";

// ── CHANGELOG ────────────────────────────────────────────────
const CHANGELOG = [
  { version:"1.7.9", date:"2026-08-12", notes:"Restored row checkboxes with proper click handling alongside clickable rows — Export Selected is back. Added a global 'Export All Categories' button that downloads one Excel file with all five tabs (Inbound, Outbound, Receiving, Storage, Transfers) as separate sheets. Fixed Deleted Entries Log stuck-loading issue. Fixed logo — restored the exact approved design (blue D, muted coral C, ops centered inside the C in green/yellow/blue) that had drifted during the 1.7.6 rebuild." },
  { version:"1.7.8", date:"2026-08-12", notes:"Removed row checkbox/bulk-select column (superseded by clickable rows) — simplified to Export All. Fixed Deleted Entries Log getting stuck on Loading (needs the _deletedEntries Firestore rule added — see setup notes)." },
  { version:"1.7.7", date:"2026-08-12", notes:"Admins can now delete locked/moved entries. Added Deleted Entries log (admin-only) tracking what was deleted, by whom, and when. Moving an entry now auto-updates the relevant date field (Date Received, Check-In Date, Ship Date, etc). Rows are now clickable anywhere to open the entry. Move and Delete buttons added inside the entry view/edit modal." },
  { version:"1.7.6", date:"2026-06-17", notes:"Added move history timeline shown inside each entry. Added Checkbox field type in Schema Editor. Moved entries now stay visible in their original tab but are locked (view-only) — toggle 'Show moved entries' in the toolbar to hide/show them." },
  { version:"1.7.5", date:"2026-06-17", notes:"Column sorting (click any header), pagination (20/50/100/all), storage status options updated (Received/In Storage/Reserved/Defective/Used), move entry now removes from original tab by default, drag-and-drop field reordering in Schema Editor." },
  { version:"1.7.0", date:"2026-06-05", notes:"Moved hosting to Cloudflare Pages. Fixed login routing issue." },
  { version:"1.6.0", date:"2026-06-05", notes:"Fixed infinite render loop and tab reset bug from v1.5. Added B-series logo (gold diamond + server stack)." },
  { version:"1.5.0", date:"2026-06-04", notes:"Added Admin Change History log. Added My View personalization (default tab, site filter, density, column visibility). Admin can set team default view." },
  { version:"1.4.0", date:"2026-06-04", notes:"Added Move Entry between tabs with audit trail. Added Export to Excel with checkbox row selection. Fixed tab order (always Inbound → Outbound → Receiving → Storage → Transfers)." },
  { version:"1.3.0", date:"2026-06-04", notes:"Added Schema Editor (add/rename/reorder/hide fields, status options). Added Site Manager (add/deactivate/reactivate sites). Added Admin Management. All 37 real DC sites loaded. PlayStation-inspired dark blue theme." },
  { version:"1.2.0", date:"2026-06-04", notes:"Added Google Sign-in via Firebase Auth. Real-time sync via Firestore. All five tabs live for 20+ users simultaneously." },
  { version:"1.1.0", date:"2026-06-04", notes:"Initial Firebase + Netlify deployment. Five tracking tabs: Inbound, Outbound, Receiving, Storage, Transfers." },
  { version:"1.0.0", date:"2026-06-04", notes:"Project created. Excel spreadsheet and React portal scaffolded with all fields, dropdowns, and DC site structure." },
];

// ── LOGO ─────────────────────────────────────────────────────
const DCopsLogo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200">
    <rect width="200" height="200" rx="18" fill="#0d0800"/>
    <rect x="1.5" y="1.5" width="197" height="197" rx="17" fill="none" stroke="#2a1800" strokeWidth="1.5"/>

    {/* Outer diamond facet lines */}
    <line x1="100" y1="14" x2="186" y2="100" stroke="#F59E0B" strokeWidth="2.5"/>
    <line x1="186" y1="100" x2="100" y2="186" stroke="#D97706" strokeWidth="2.5"/>
    <line x1="100" y1="186" x2="14" y2="100" stroke="#D97706" strokeWidth="2.5"/>
    <line x1="14" y1="100" x2="100" y2="14" stroke="#F59E0B" strokeWidth="2.5"/>
    <polygon points="100,14 186,100 100,186 14,100" fill="#0d0800"/>
    <line x1="100" y1="14" x2="186" y2="100" stroke="#92400E" strokeWidth="1" opacity="0.4"/>
    <line x1="186" y1="100" x2="100" y2="186" stroke="#92400E" strokeWidth="1" opacity="0.4"/>
    <line x1="100" y1="186" x2="14" y2="100" stroke="#92400E" strokeWidth="1" opacity="0.4"/>
    <line x1="14" y1="100" x2="100" y2="14" stroke="#92400E" strokeWidth="1" opacity="0.4"/>

    {/* Inner amber ring */}
    <polygon points="100,36 164,100 100,164 36,100" fill="#0d0800" stroke="#92400E" strokeWidth="1"/>

    {/* Server stack — 5 rows centered */}
    <rect x="66" y="65" width="68" height="10" rx="2" fill="#1a3a6c"/>
    <rect x="68" y="66" width="64" height="8" rx="1.5" fill="#0d2244"/>
    <rect x="68" y="66" width="64" height="8" rx="1.5" fill="none" stroke="#4B9FE1" strokeWidth="0.7"/>
    <circle cx="123" cy="70" r="2" fill="#4ADE80"/>
    <circle cx="117" cy="70" r="2" fill="#4ADE80"/>

    <rect x="66" y="79" width="68" height="10" rx="2" fill="#0d2244" stroke="#2a4a7a" strokeWidth="0.7"/>
    <circle cx="123" cy="84" r="1.7" fill="#4ADE80"/>
    <circle cx="117" cy="84" r="1.7" fill="#FBB954"/>

    {/* DCOPS — highlighted middle row, gold */}
    <rect x="64" y="93" width="72" height="14" rx="2.5" fill="#1a0e00" stroke="#F59E0B" strokeWidth="1.3"/>
    <rect x="64" y="93" width="4" height="14" rx="1" fill="#F59E0B"/>
    <rect x="132" y="93" width="4" height="14" rx="1" fill="#F59E0B"/>
    <text x="100" y="103" textAnchor="middle" fill="#FCD34D" fontSize="8" fontWeight="900" fontFamily="Arial,sans-serif" letterSpacing="1.5">DCOPS</text>
    <circle cx="123" cy="100" r="2" fill="#4ADE80"/>

    <rect x="66" y="111" width="68" height="10" rx="2" fill="#0d2244" stroke="#2a4a7a" strokeWidth="0.7"/>
    <circle cx="123" cy="116" r="1.7" fill="#4ADE80"/>
    <circle cx="117" cy="116" r="1.7" fill="#4ADE80"/>

    <rect x="66" y="125" width="68" height="10" rx="2" fill="#0d2244" stroke="#2a4a7a" strokeWidth="0.7"/>
    <circle cx="123" cy="130" r="1.7" fill="#EF4444"/>
    <circle cx="117" cy="130" r="1.7" fill="#4ADE80"/>

    <line x1="66" y1="63" x2="134" y2="63" stroke="#2a1800" strokeWidth="1"/>
    <line x1="66" y1="137" x2="134" y2="137" stroke="#2a1800" strokeWidth="1"/>

    {/* Diamond tip arrows */}
    <polygon points="100,14 105,26 100,34 95,26" fill="#F59E0B"/>
    <polygon points="186,100 174,105 166,100 174,95" fill="#D97706"/>
    <polygon points="100,186 95,174 100,166 105,174" fill="#D97706"/>
    <polygon points="14,100 26,95 34,100 26,105" fill="#F59E0B"/>
  </svg>
);

// ── DEFAULT SCHEMAS ──────────────────────────────────────────
const DEFAULT_SCHEMAS = {
  inbound: {
    label:"Inbound", icon:"📥", color:"#38BDF8",
    statusField:"status",
    statusOptions:["Pending","In Transit","Received","Issue - Damaged","Issue - Missing","On Hold"],
    fields:[
      {key:"poNumber",     label:"PO / Order #",          type:"text",   showInTable:true},
      {key:"vendor",       label:"Vendor / Manufacturer", type:"text",   showInTable:true},
      {key:"item",         label:"Item Description",      type:"text",   showInTable:true, wide:true},
      {key:"serial",       label:"Serial Number",         type:"text",   showInTable:false},
      {key:"assetTag",     label:"Asset Tag",             type:"text",   showInTable:false},
      {key:"qty",          label:"Qty",                   type:"number", showInTable:true},
      {key:"carrier",      label:"Carrier",               type:"text",   showInTable:false},
      {key:"tracking",     label:"Tracking #",            type:"text",   showInTable:false},
      {key:"expectedDate", label:"Expected Delivery",     type:"date",   showInTable:true},
      {key:"actualDate",   label:"Actual Delivery",       type:"date",   showInTable:true},
      {key:"site",         label:"DC Site",               type:"site",   showInTable:true},
      {key:"location",     label:"DC Location",           type:"text",   showInTable:true},
      {key:"receivedBy",   label:"Received By",           type:"text",   showInTable:true},
      {key:"condition",    label:"Condition",             type:"select", showInTable:true, options:["Good","Damaged","Incomplete","Needs Inspection"]},
      {key:"psd",          label:"PSD / Project",         type:"text",   showInTable:true},
      {key:"status",       label:"Status",                type:"select", showInTable:true, options:["Pending","In Transit","Received","Issue - Damaged","Issue - Missing","On Hold"]},
      {key:"comments",     label:"Comments",              type:"text",   showInTable:false, wide:true},
    ],
  },
  outbound: {
    label:"Outbound", icon:"📤", color:"#F87171",
    statusField:"status",
    statusOptions:["Pending","Packaged","Shipped","Delivered","Returned","Issue"],
    fields:[
      {key:"poNumber",    label:"PO / Order #",            type:"text",   showInTable:true},
      {key:"destination", label:"Destination / Recipient", type:"text",   showInTable:true, wide:true},
      {key:"item",        label:"Item Description",        type:"text",   showInTable:true, wide:true},
      {key:"serial",      label:"Serial Number",           type:"text",   showInTable:false},
      {key:"assetTag",    label:"Asset Tag",               type:"text",   showInTable:false},
      {key:"qty",         label:"Qty",                     type:"number", showInTable:true},
      {key:"carrier",     label:"Carrier",                 type:"text",   showInTable:true},
      {key:"tracking",    label:"Tracking #",              type:"text",   showInTable:true},
      {key:"shipDate",    label:"Ship Date",               type:"date",   showInTable:true},
      {key:"estArrival",  label:"Est. Arrival",            type:"date",   showInTable:true},
      {key:"site",        label:"Origin DC Site",          type:"site",   showInTable:true},
      {key:"location",    label:"Origin Location",         type:"text",   showInTable:false},
      {key:"shippedBy",   label:"Shipped By",              type:"text",   showInTable:true},
      {key:"psd",         label:"PSD / Project",           type:"text",   showInTable:true},
      {key:"status",      label:"Status",                  type:"select", showInTable:true, options:["Pending","Packaged","Shipped","Delivered","Returned","Issue"]},
      {key:"comments",    label:"Comments",                type:"text",   showInTable:false, wide:true},
    ],
  },
  receiving: {
    label:"Receiving", icon:"✅", color:"#4ADE80",
    statusField:"condition",
    statusOptions:["Good","Damaged","Incomplete","Needs Inspection","Refused"],
    fields:[
      {key:"dateReceived",  label:"Date Received",    type:"date",   showInTable:true},
      {key:"time",          label:"Time",             type:"text",   showInTable:true},
      {key:"poNumber",      label:"PO / Order #",     type:"text",   showInTable:true},
      {key:"vendor",        label:"Vendor",           type:"text",   showInTable:true},
      {key:"item",          label:"Item Description", type:"text",   showInTable:true, wide:true},
      {key:"serial",        label:"Serial Number",    type:"text",   showInTable:false},
      {key:"assetTag",      label:"Asset Tag",        type:"text",   showInTable:false},
      {key:"qty",           label:"Qty",              type:"number", showInTable:true},
      {key:"site",          label:"DC Site",          type:"site",   showInTable:true},
      {key:"receivedBy",    label:"Received By",      type:"text",   showInTable:true},
      {key:"carrier",       label:"Carrier",          type:"text",   showInTable:false},
      {key:"tracking",      label:"Tracking #",       type:"text",   showInTable:false},
      {key:"condition",     label:"Condition",        type:"select", showInTable:true, options:["Good","Damaged","Incomplete","Needs Inspection","Refused"]},
      {key:"psd",           label:"PSD / Project",    type:"text",   showInTable:true},
      {key:"stagedLocation",label:"Staged Location",  type:"text",   showInTable:true},
      {key:"comments",      label:"Comments",         type:"text",   showInTable:false, wide:true},
    ],
  },
  storage: {
    label:"Storage", icon:"🗄️", color:"#C084FC",
    statusField:"status",
    statusOptions:["Received","In Storage","Reserved","Defective","Used"],
    fields:[
      {key:"assetId",      label:"Asset ID",                     type:"text",   showInTable:true},
      {key:"item",         label:"Item Description",             type:"text",   showInTable:true, wide:true},
      {key:"category",     label:"Category",                     type:"select", showInTable:true, options:["Server","NIC/HBA","Storage","Networking","Cable","Power","Memory","CPU","GPU","Misc"]},
      {key:"vendor",       label:"Vendor / Manufacturer",        type:"text",   showInTable:true},
      {key:"model",        label:"Model / Part #",               type:"text",   showInTable:false},
      {key:"serial",       label:"Serial Number",                type:"text",   showInTable:true},
      {key:"assetTag",     label:"Asset Tag",                    type:"text",   showInTable:true},
      {key:"qty",          label:"Qty",                          type:"number", showInTable:true},
      {key:"site",         label:"DC Site",                      type:"site",   showInTable:true},
      {key:"location",     label:"DC Location (Row/Rack/Shelf)", type:"text",   showInTable:true},
      {key:"psd",          label:"PSD / Project",                type:"text",   showInTable:true},
      {key:"dateReceived", label:"Date Received",                type:"date",   showInTable:false},
      {key:"status",       label:"Status",                       type:"select", showInTable:true, options:["Received","In Storage","Reserved","Defective","Used"]},
      {key:"condition",    label:"Condition",                    type:"select", showInTable:false, options:["Good","Damaged","Incomplete","Needs Inspection"]},
      {key:"checkedOutBy", label:"Checked Out By",               type:"text",   showInTable:true},
      {key:"checkOutDate", label:"Check-Out Date",               type:"date",   showInTable:true},
      {key:"checkInDate",  label:"Check-In Date",                type:"date",   showInTable:false},
      {key:"poNumber",     label:"PO / Order #",                 type:"text",   showInTable:false},
      {key:"comments",     label:"Comments",                     type:"text",   showInTable:false, wide:true},
    ],
  },
  transfers: {
    label:"Transfers", icon:"🔀", color:"#FBB954",
    statusField:"status",
    statusOptions:["Requested","Approved","Packed","In Transit","Received","Complete","Cancelled"],
    fields:[
      {key:"transferDate", label:"Transfer Date",    type:"date",   showInTable:true},
      {key:"item",         label:"Item Description", type:"text",   showInTable:true, wide:true},
      {key:"serial",       label:"Serial Number",    type:"text",   showInTable:false},
      {key:"assetTag",     label:"Asset Tag",        type:"text",   showInTable:false},
      {key:"qty",          label:"Qty",              type:"number", showInTable:true},
      {key:"fromSite",     label:"From DC Site",     type:"site",   showInTable:true},
      {key:"fromLocation", label:"From Location",    type:"text",   showInTable:true},
      {key:"toSite",       label:"To DC Site",       type:"site",   showInTable:true},
      {key:"toLocation",   label:"To Location",      type:"text",   showInTable:true},
      {key:"requestedBy",  label:"Requested By",     type:"text",   showInTable:true},
      {key:"approvedBy",   label:"Approved By",      type:"text",   showInTable:false},
      {key:"carrier",      label:"Carrier / Method", type:"text",   showInTable:false},
      {key:"tracking",     label:"Tracking #",       type:"text",   showInTable:false},
      {key:"psd",          label:"PSD / Project",    type:"text",   showInTable:true},
      {key:"status",       label:"Status",           type:"select", showInTable:true, options:["Requested","Approved","Packed","In Transit","Received","Complete","Cancelled"]},
      {key:"comments",     label:"Comments",         type:"text",   showInTable:false, wide:true},
    ],
  },
};

// Which date field to auto-update when an entry MOVES INTO this tab
const DATE_FIELD_ON_MOVE = {
  inbound: "actualDate",
  outbound: "shipDate",
  receiving: "dateReceived",
  storage: "checkInDate",
  transfers: "transferDate",
};

const DEFAULT_SITES = SITES.map(code => ({
  code, active: true,
  name: SITE_DETAILS[code]?.name || code,
  city: SITE_DETAILS[code]?.city || "",
  country: SITE_DETAILS[code]?.country || "",
}));

const STATUS_STYLES = {
  "Received":{bg:"#D5F5E3",color:"#1E8449"},
  "Delivered":{bg:"#D5F5E3",color:"#1E8449"},
  "Complete":{bg:"#D5F5E3",color:"#1E8449"},
  "Good":{bg:"#D5F5E3",color:"#1E8449"},
  "In Storage":{bg:"#DBEAFE",color:"#1D4ED8"},
  "In Transit":{bg:"#DBEAFE",color:"#1D4ED8"},
  "Shipped":{bg:"#DBEAFE",color:"#1D4ED8"},
  "Pending":{bg:"#FEF9C3",color:"#854D0E"},
  "Requested":{bg:"#FEF9C3",color:"#854D0E"},
  "Approved":{bg:"#FEF9C3",color:"#854D0E"},
  "Packaged":{bg:"#FEF9C3",color:"#854D0E"},
  "Reserved":{bg:"#F3E8FF",color:"#6B21A8"},
  "Used":{bg:"#F3E8FF",color:"#6B21A8"},
  "Issue":{bg:"#FEE2E2",color:"#991B1B"},
  "Issue - Damaged":{bg:"#FEE2E2",color:"#991B1B"},
  "Issue - Missing":{bg:"#FEE2E2",color:"#991B1B"},
  "Damaged":{bg:"#FEE2E2",color:"#991B1B"},
  "Defective":{bg:"#FEE2E2",color:"#991B1B"},
  "On Hold":{bg:"#FFEDD5",color:"#9A3412"},
  "Cancelled":{bg:"#FFEDD5",color:"#9A3412"},
  "Moved":{bg:"#1E293B",color:"#64748B"},
};

const inp = {width:"100%",padding:"8px 10px",borderRadius:6,border:"1px solid #1a3a5c",fontSize:13,background:"#071428",color:"#E2E8F0",outline:"none",boxSizing:"border-box"};
const btn = (bg,color)=>({padding:"8px 20px",borderRadius:6,border:"none",background:bg,color,fontWeight:700,cursor:"pointer",fontSize:13});

function StatusBadge({value}){
  if(!value) return <span style={{color:"#94A3B8",fontSize:11}}>—</span>;
  const s=STATUS_STYLES[value]||{bg:"#1E293B",color:"#94A3B8"};
  return <span style={{background:s.bg,color:s.color,borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{value}</span>;
}

function SiteBadge({code,sites}){
  if(!code) return <span style={{color:"#475569"}}>—</span>;
  const s=sites.find(x=>x.code===code);
  const tip=s?`${s.name} · ${s.city}, ${s.country}`:code;
  return <span title={tip} style={{borderBottom:"1px dotted #1a5a8a",cursor:"help",color:"#7DD3FC"}}>{code}</span>;
}

function formatDateTime(iso){
  if(!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"}) + " " +
         d.toLocaleTimeString(undefined,{hour:"2-digit",minute:"2-digit"});
}

// ── ENTRY MODAL ──────────────────────────────────────────────
function EntryModal({schema,item,user,sites,readOnly,isAdmin,onSave,onClose,onMoveClick,onDeleteClick}){
  const [form,setForm]=useState(item?{...item}:{});
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const activeSites=sites.filter(s=>s.active);
  const history = item?._moveHistory || [];

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:820,maxHeight:"90vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)"}}>
          <div>
            <div style={{color:"#E2E8F0",fontWeight:700,fontSize:15,display:"flex",alignItems:"center",gap:8}}>
              {readOnly && <span style={{fontSize:13}}>🔒</span>}
              {item ? (readOnly ? "View Entry (Locked)" : "Edit Entry") : "New Entry"}
            </div>
            <div style={{color:"#64748B",fontSize:11,marginTop:2}}>
              {readOnly ? "This entry has been moved and can no longer be edited here." : item ? `Last edited by ${item.lastEditedBy||"unknown"}` : `Adding as ${user?.displayName}`}
            </div>
          </div>
          <button onClick={onClose} style={{background:"#1E293B",border:"none",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>

        <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px 20px"}}>
            {schema.fields.map(f=>(
              <div key={f.key} style={{gridColumn:f.wide?"1 / -1":"auto"}}>
                <label style={{display:"block",fontSize:10,fontWeight:700,color:"#64748B",marginBottom:5,textTransform:"uppercase",letterSpacing:0.8}}>{f.label}</label>
                {f.type==="checkbox" ? (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0"}}>
                    <input type="checkbox" checked={!!form[f.key]} disabled={readOnly}
                      onChange={e=>set(f.key,e.target.checked)}
                      style={{width:18,height:18,accentColor:"#4B9FE1",cursor:readOnly?"default":"pointer"}}/>
                    <span style={{color:"#CBD5E1",fontSize:13}}>{form[f.key] ? "Yes" : "No"}</span>
                  </div>
                ) : f.type==="site" ? (
                  <select value={form[f.key]||""} disabled={readOnly} onChange={e=>set(f.key,e.target.value)} style={inp}>
                    <option value="">— Select Site —</option>
                    {activeSites.map(s=>(
                      <option key={s.code} value={s.code}>{s.code} — {s.name}, {s.city}</option>
                    ))}
                  </select>
                ) : f.type==="select" ? (
                  <select value={form[f.key]||""} disabled={readOnly} onChange={e=>set(f.key,e.target.value)} style={inp}>
                    <option value="">— Select —</option>
                    {(f.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type==="date"?"date":f.type==="number"?"number":"text"} value={form[f.key]||""} disabled={readOnly}
                    onChange={e=>set(f.key,e.target.value)} style={{...inp,...(readOnly?{opacity:0.6,cursor:"not-allowed"}:{})}}/>
                )}
              </div>
            ))}
          </div>

          {history.length > 0 && (
            <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid #1a3a5c"}}>
              <div style={{color:"#4a7fa5",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>📜 Movement History</div>
              <div style={{display:"flex",flexDirection:"column",gap:0}}>
                {history.map((h,i)=>(
                  <div key={i} style={{display:"flex",gap:12,paddingBottom:i<history.length-1?14:0,position:"relative"}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:12}}>
                      <div style={{width:9,height:9,borderRadius:"50%",background:"#4B9FE1",flexShrink:0,marginTop:3}}/>
                      {i<history.length-1 && <div style={{width:1.5,flex:1,background:"#1a3a5c",marginTop:2}}/>}
                    </div>
                    <div style={{paddingBottom:4}}>
                      <div style={{color:"#CBD5E1",fontSize:12}}>
                        Moved from <strong style={{color:"#7DD3FC"}}>{h.from}</strong> → <strong style={{color:"#7DD3FC"}}>{h.to}</strong>
                      </div>
                      <div style={{color:"#475569",fontSize:11,marginTop:2}}>{formatDateTime(h.at)} · by {h.by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",gap:10,justifyContent:"space-between",alignItems:"center",background:"#071428"}}>
          <div style={{display:"flex",gap:8}}>
            {item && !readOnly && onMoveClick && (
              <button onClick={()=>onMoveClick(item)} style={{padding:"7px 14px",borderRadius:6,border:"1px solid #1a3a6c",background:"transparent",color:"#7DD3FC",cursor:"pointer",fontSize:12}}>🔄 Move</button>
            )}
            {item && (!readOnly || isAdmin) && onDeleteClick && (
              <button onClick={()=>onDeleteClick(item)} style={{padding:"7px 14px",borderRadius:6,border:"1px solid #450A0A",background:"transparent",color:"#F87171",cursor:"pointer",fontSize:12}}>
                {readOnly ? "🗑 Admin Delete" : "🗑 Delete"}
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose} style={{...btn("transparent","#94A3B8"),border:"1px solid #1a3a5c"}}>{readOnly?"Close":"Cancel"}</button>
            {!readOnly && (
              <button onClick={async()=>{setSaving(true);await onSave(form);setSaving(false);}} disabled={saving}
                style={btn(saving?"#1a3a5c":"#38BDF8", saving?"#64748B":"#0F172A")}>
                {saving?"Saving...":"Save Entry"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MOVE ENTRY MODAL ─────────────────────────────────────────
function MoveModal({entry,currentTab,schemas,onMove,onClose}){
  const [targetTab,setTargetTab]=useState("");
  const [keepOriginal,setKeepOriginal]=useState(true);
  const [moving,setMoving]=useState(false);

  const TAB_ORDER = ["inbound","outbound","receiving","storage","transfers"];
  const tabOptions = TAB_ORDER.filter(k=>k!==currentTab && schemas[k]);

  const getFieldPreview=()=>{
    if(!targetTab) return null;
    const destFields=schemas[targetTab].fields.map(f=>f.key);
    const sourceFields=Object.keys(entry).filter(k=>!["id","createdAt","createdBy","lastEditedAt","lastEditedBy","_moveHistory","_locked"].includes(k));
    const matched=sourceFields.filter(k=>destFields.includes(k)&&entry[k]);
    const unmatched=sourceFields.filter(k=>!destFields.includes(k)&&entry[k]);
    return {matched,unmatched};
  };
  const preview=targetTab?getFieldPreview():null;

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.9)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:520,border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",background:"linear-gradient(90deg,#071428,#0a1f3d)",borderRadius:"12px 12px 0 0"}}>
          <div style={{color:"#E2E8F0",fontWeight:800,fontSize:15}}>🔄 Move Entry</div>
          <div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>Move this entry to another tracker tab</div>
        </div>
        <div style={{padding:"20px 24px"}}>
          <div style={{background:"#071428",border:"1px solid #1a3a5c",borderRadius:8,padding:"12px 14px",marginBottom:18}}>
            <div style={{color:"#4a7fa5",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>Entry</div>
            <div style={{color:"#E2E8F0",fontSize:13,fontWeight:600}}>{entry.item||entry.description||"—"}</div>
            {entry.serial && <div style={{color:"#64748B",fontSize:11,marginTop:2}}>Serial: {entry.serial}</div>}
            {entry.poNumber && <div style={{color:"#64748B",fontSize:11}}>PO: {entry.poNumber}</div>}
            {entry.site && <div style={{color:"#7DD3FC",fontSize:11}}>Site: {entry.site}</div>}
          </div>

          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#4a7fa5",marginBottom:8,textTransform:"uppercase",letterSpacing:0.8}}>Move to</label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {tabOptions.map(k=>{
                const s=schemas[k];
                return(
                  <button key={k} onClick={()=>setTargetTab(k)} style={{
                    padding:"12px 14px",borderRadius:8,cursor:"pointer",textAlign:"left",
                    border:`2px solid ${targetTab===k?s.color:"#1a3a5c"}`,
                    background:targetTab===k?`${s.color}18`:"#071428",transition:"all 0.15s"
                  }}>
                    <div style={{fontSize:16,marginBottom:3}}>{s.icon}</div>
                    <div style={{color:targetTab===k?s.color:"#94A3B8",fontWeight:700,fontSize:13}}>{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {preview && (
            <div style={{background:"#071428",border:"1px solid #1a3a5c",borderRadius:8,padding:"12px 14px",marginBottom:16}}>
              <div style={{color:"#4a7fa5",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Field Preview</div>
              {preview.matched.length>0 && (
                <div style={{marginBottom:6}}>
                  <span style={{color:"#4ADE80",fontSize:11,fontWeight:700}}>✓ Carrying over: </span>
                  <span style={{color:"#94A3B8",fontSize:11}}>{preview.matched.join(", ")}</span>
                </div>
              )}
              {preview.unmatched.length>0 && (
                <div>
                  <span style={{color:"#FBB954",fontSize:11,fontWeight:700}}>⚠ Not in destination: </span>
                  <span style={{color:"#64748B",fontSize:11}}>{preview.unmatched.join(", ")}</span>
                </div>
              )}
            </div>
          )}

          <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8,padding:"10px 14px",background:"#071428",border:"1px solid #1a3a5c",borderRadius:8}}>
            <input type="checkbox" id="keepOrig" checked={keepOriginal} onChange={e=>setKeepOriginal(e.target.checked)}
              style={{accentColor:"#0070CC",width:16,height:16,cursor:"pointer",marginTop:2}}/>
            <label htmlFor="keepOrig" style={{color:"#CBD5E1",fontSize:13,cursor:"pointer"}}>
              Keep original entry, view-only (recommended)
            </label>
          </div>
          <div style={{color:"#475569",fontSize:11,marginBottom:16,paddingLeft:4}}>
            {keepOriginal
              ? "Original stays visible in this tab but locked — it can't be edited, only viewed. Full move history is preserved."
              : "Original entry will be permanently deleted from this tab after moving."}
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={onClose} style={{padding:"8px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Cancel</button>
            <button
              onClick={async()=>{if(!targetTab)return;setMoving(true);await onMove(entry,targetTab,keepOriginal);setMoving(false);}}
              disabled={!targetTab||moving}
              style={{padding:"8px 24px",borderRadius:6,border:"none",background:!targetTab||moving?"#1a3a5c":"#0070CC",color:!targetTab||moving?"#475569":"#fff",fontWeight:700,cursor:!targetTab||moving?"default":"pointer",fontSize:13}}
            >{moving?"Moving...":"Move Entry →"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SITE MANAGER ─────────────────────────────────────────────
function SiteManager({sites,onUpdate,onClose}){
  const [list,setList]=useState(JSON.parse(JSON.stringify(sites)));
  const [newSite,setNewSite]=useState({code:"",name:"",city:"",country:""});
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState("");
  const [filter,setFilter]=useState("all");

  const toggle=(idx)=>setList(l=>{const n=[...l];n[idx]={...n[idx],active:!n[idx].active};return n;});
  const remove=(idx)=>{
    if(!window.confirm("Remove this site? Historical entries will be preserved."))return;
    setList(l=>l.filter((_,i)=>i!==idx));
  };
  const add=()=>{
    if(!newSite.code.trim()||!newSite.name.trim()){setMsg("Site code and name are required");return;}
    if(list.find(s=>s.code===newSite.code.trim())){setMsg("Site code already exists");return;}
    setList(l=>[...l,{...newSite,code:newSite.code.trim().toUpperCase(),active:true}]);
    setNewSite({code:"",name:"",city:"",country:""});
    setMsg("Site added — click Save to publish.");
  };
  const save=async()=>{setSaving(true);await onUpdate(list);setSaving(false);setMsg("✅ Sites saved and live for everyone.");};

  const visible=list.filter(s=>filter==="all"||(filter==="active"&&s.active)||(filter==="inactive"&&!s.active));
  const regions={USA:0,UK:0,Germany:0,France:0,Spain:0,Italy:0,Sweden:0,Japan:0};
  list.filter(s=>s.active).forEach(s=>{if(regions[s.country]!==undefined)regions[s.country]++;});

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:900,maxHeight:"92vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.25)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)"}}>
          <div>
            <div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>🏢 Site Manager</div>
            <div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>{list.filter(s=>s.active).length} active · {list.filter(s=>!s.active).length} inactive · {list.length} total</div>
          </div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:20}}>
            {Object.entries(regions).filter(([,v])=>v>0).map(([country,count])=>(
              <div key={country} style={{background:"#071428",border:"1px solid #1a3a5c",borderRadius:6,padding:"6px 12px",fontSize:11}}>
                <span style={{color:"#4a7fa5"}}>{country}</span><span style={{color:"#7DD3FC",fontWeight:700,marginLeft:6}}>{count}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:6,marginBottom:14}}>
            {["all","active","inactive"].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{padding:"5px 14px",borderRadius:5,border:`1px solid ${filter===f?"#0070CC":"#1a3a5c"}`,background:filter===f?"#0070CC":"transparent",color:filter===f?"#fff":"#4a7fa5",cursor:"pointer",fontSize:12,textTransform:"capitalize"}}>{f}</button>
            ))}
          </div>
          <div style={{border:"1px solid #1a3a5c",borderRadius:8,overflow:"hidden",marginBottom:24}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:"#071428"}}>
                {["Code","Facility Name","City","Country","Status","Actions"].map(h=><th key={h} style={{padding:"8px 12px",color:"#4a7fa5",textAlign:"left",fontWeight:700,fontSize:10,textTransform:"uppercase",letterSpacing:0.5}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {visible.map((s,i)=>(
                  <tr key={s.code} style={{borderTop:"1px solid #1a3a5c",background:i%2===0?"#050e1f":"#071428",opacity:s.active?1:0.5}}>
                    <td style={{padding:"9px 12px",color:"#7DD3FC",fontWeight:700}}>{s.code}</td>
                    <td style={{padding:"9px 12px",color:"#CBD5E1"}}>{s.name}</td>
                    <td style={{padding:"9px 12px",color:"#94A3B8"}}>{s.city}</td>
                    <td style={{padding:"9px 12px",color:"#94A3B8"}}>{s.country}</td>
                    <td style={{padding:"9px 12px"}}>
                      <span style={{background:s.active?"#D5F5E3":"#1E293B",color:s.active?"#1E8449":"#64748B",borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700}}>{s.active?"Active":"Inactive"}</span>
                    </td>
                    <td style={{padding:"6px 12px",whiteSpace:"nowrap"}}>
                      <button onClick={()=>toggle(list.indexOf(s))} style={{padding:"3px 10px",borderRadius:4,border:`1px solid ${s.active?"#450A0A":"#1a4a1a"}`,background:"transparent",color:s.active?"#F87171":"#4ADE80",cursor:"pointer",fontSize:11,marginRight:4}}>{s.active?"Deactivate":"Reactivate"}</button>
                      <button onClick={()=>remove(list.indexOf(s))} style={{padding:"3px 10px",borderRadius:4,border:"1px solid #450A0A",background:"transparent",color:"#F87171",cursor:"pointer",fontSize:11}}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>Add New Site</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
            {[["code","Site Code (e.g. LAXA-S4)",100],["name","Facility Name",200],["city","City",150],["country","Country",120]].map(([k,ph,w])=>(
              <div key={k}>
                <label style={{display:"block",fontSize:10,color:"#4a7fa5",marginBottom:4,textTransform:"capitalize"}}>{k}</label>
                <input value={newSite[k]} onChange={e=>setNewSite(n=>({...n,[k]:e.target.value}))} placeholder={ph} style={{...inp,width:w}} onKeyDown={e=>e.key==="Enter"&&add()}/>
              </div>
            ))}
            <button onClick={add} style={btn("#0070CC","#fff")}>+ Add Site</button>
          </div>
          {msg&&<div style={{marginTop:10,color:msg.startsWith("✅")?"#4ADE80":"#FBB954",fontSize:12}}>{msg}</div>}
        </div>
        <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",gap:10,background:"#071428"}}>
          <button onClick={onClose} style={{...btn("transparent","#94A3B8"),border:"1px solid #1a3a5c"}}>Close</button>
          <button onClick={save} disabled={saving} style={btn(saving?"#1a3a5c":"#4ADE80","#071428")}>{saving?"Saving...":"💾 Save & Publish"}</button>
        </div>
      </div>
    </div>
  );
}

// ── SCHEMA EDITOR ────────────────────────────────────────────
function AddInline({placeholder,onAdd}){
  const [val,setVal]=useState("");
  return(
    <div style={{display:"flex",gap:6}}>
      <input value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder}
        onKeyDown={e=>{if(e.key==="Enter"&&val.trim()){onAdd(val.trim());setVal("");}}}
        style={{...inp,width:220,fontSize:12,padding:"5px 8px"}}/>
      <button onClick={()=>{if(val.trim()){onAdd(val.trim());setVal("");}}} style={{padding:"5px 12px",borderRadius:5,border:"none",background:"#1a3a5c",color:"#CBD5E1",cursor:"pointer",fontSize:12}}>Add</button>
    </div>
  );
}

function SchemaEditor({schemas,onUpdate,onClose}){
  const TAB_ORDER_ED = ["inbound","outbound","receiving","storage","transfers"];
  const [activeTab,setActiveTab]=useState("inbound");
  const [local,setLocal]=useState(JSON.parse(JSON.stringify(schemas)));
  const [saving,setSaving]=useState(false);
  const [newField,setNewField]=useState({label:"",type:"text",options:""});
  const [msg,setMsg]=useState("");
  const schema=local[activeTab];
  const tabKeys = TAB_ORDER_ED.filter(k=>local[k]);

  const updateField=(idx,k,v)=>setLocal(s=>{const n=JSON.parse(JSON.stringify(s));n[activeTab].fields[idx][k]=v;return n;});
  const removeField=(idx)=>setLocal(s=>{const n=JSON.parse(JSON.stringify(s));n[activeTab].fields.splice(idx,1);return n;});

  const addField=()=>{
    if(!newField.label.trim()){setMsg("Label required");return;}
    const key=newField.label.toLowerCase().replace(/[^a-z0-9]/g,"_").replace(/_+/g,"_");
    setLocal(s=>{
      const n=JSON.parse(JSON.stringify(s));
      const opts=newField.type==="select"?newField.options.split(",").map(x=>x.trim()).filter(Boolean):undefined;
      n[activeTab].fields.push({key,label:newField.label,type:newField.type,showInTable:false,wide:false,...(opts?{options:opts}:{})});
      return n;
    });
    setNewField({label:"",type:"text",options:""});
    setMsg("Field added — save to publish.");
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:920,maxHeight:"92vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.25)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)"}}>
          <div>
            <div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>⚙️ Schema Editor</div>
            <div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>Add, rename, reorder (drag ⠿), or hide fields. Changes are instant for all users.</div>
          </div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{display:"flex",gap:2,padding:"0 24px",borderBottom:"1px solid #1a3a5c",background:"#071428"}}>
          {tabKeys.map(k=>{
            const s=local[k];
            return <button key={k} onClick={()=>setActiveTab(k)} style={{padding:"10px 16px",border:"none",cursor:"pointer",borderRadius:"6px 6px 0 0",background:activeTab===k?"#050e1f":"transparent",color:activeTab===k?s.color:"#475569",fontWeight:activeTab===k?700:500,fontSize:12,borderBottom:activeTab===k?`2px solid ${s.color}`:"2px solid transparent"}}>{s.icon} {s.label}</button>;
          })}
        </div>
        <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
          <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>Status Options</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
            {schema.statusOptions.map((opt,i)=>(
              <span key={i} style={{background:"#071428",color:"#CBD5E1",border:"1px solid #1a3a5c",borderRadius:4,padding:"4px 10px",fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                {opt}
                <button onClick={()=>setLocal(s=>{const n=JSON.parse(JSON.stringify(s));n[activeTab].statusOptions.splice(i,1);return n;})} style={{background:"none",border:"none",color:"#F87171",cursor:"pointer",fontSize:14,lineHeight:1,padding:0}}>×</button>
              </span>
            ))}
          </div>
          <AddInline placeholder="New status option..." onAdd={val=>setLocal(s=>{const n=JSON.parse(JSON.stringify(s));if(!n[activeTab].statusOptions.includes(val))n[activeTab].statusOptions.push(val);return n;})}/>

          <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,margin:"20px 0 10px"}}>Fields <span style={{color:"#334155",fontWeight:400,textTransform:"none"}}>— drag ⠿ to reorder</span></div>
          <div style={{border:"1px solid #1a3a5c",borderRadius:8,overflow:"hidden",marginBottom:20}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:"#071428"}}>
                {["","Label","Type","Show in Table","Wide","Actions"].map(h=><th key={h} style={{padding:"8px 12px",color:"#4a7fa5",textAlign:"left",fontWeight:700,fontSize:10,textTransform:"uppercase"}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {schema.fields.map((f,i)=>(
                  <tr key={f.key}
                    draggable
                    onDragStart={e=>{e.dataTransfer.setData("text/plain",String(i));e.currentTarget.style.opacity="0.4";}}
                    onDragEnd={e=>{e.currentTarget.style.opacity="1";}}
                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#0a1f3d";}}
                    onDragLeave={e=>{e.currentTarget.style.background=i%2===0?"#050e1f":"#071428";}}
                    onDrop={e=>{
                      e.preventDefault();
                      e.currentTarget.style.background=i%2===0?"#050e1f":"#071428";
                      const from=parseInt(e.dataTransfer.getData("text/plain"));
                      const to=i;
                      if(from===to) return;
                      setLocal(s=>{
                        const n=JSON.parse(JSON.stringify(s));
                        const fields=n[activeTab].fields;
                        const [moved]=fields.splice(from,1);
                        fields.splice(to,0,moved);
                        return n;
                      });
                    }}
                    style={{borderTop:"1px solid #1a3a5c",background:i%2===0?"#050e1f":"#071428",cursor:"grab"}}>
                    <td style={{padding:"8px 12px"}}>
                      <span style={{color:"#4a7fa5",fontSize:16,cursor:"grab",userSelect:"none"}} title="Drag to reorder">⠿</span>
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      <input value={f.label} onChange={e=>updateField(i,"label",e.target.value)} style={{background:"#071428",border:"1px solid #1a3a5c",color:"#E2E8F0",borderRadius:4,padding:"4px 8px",fontSize:12,width:170}}/>
                    </td>
                    <td style={{padding:"8px 12px",color:"#4a7fa5"}}>{f.type}</td>
                    <td style={{padding:"8px 12px"}}><input type="checkbox" checked={!!f.showInTable} onChange={e=>updateField(i,"showInTable",e.target.checked)} style={{accentColor:"#0070CC",width:16,height:16,cursor:"pointer"}}/></td>
                    <td style={{padding:"8px 12px"}}><input type="checkbox" checked={!!f.wide} onChange={e=>updateField(i,"wide",e.target.checked)} style={{accentColor:"#0070CC",width:16,height:16,cursor:"pointer"}}/></td>
                    <td style={{padding:"8px 12px"}}><button onClick={()=>removeField(i)} style={{background:"none",border:"1px solid #450A0A",color:"#F87171",borderRadius:4,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10}}>Add New Field</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
            <div><label style={{display:"block",fontSize:10,color:"#4a7fa5",marginBottom:4}}>Label</label><input value={newField.label} onChange={e=>setNewField(f=>({...f,label:e.target.value}))} placeholder="Field name" style={{...inp,width:200}}/></div>
            <div><label style={{display:"block",fontSize:10,color:"#4a7fa5",marginBottom:4}}>Type</label>
              <select value={newField.type} onChange={e=>setNewField(f=>({...f,type:e.target.value}))} style={{...inp,width:130}}>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="select">Dropdown</option>
                <option value="checkbox">Checkbox</option>
              </select>
            </div>
            {newField.type==="select"&&<div><label style={{display:"block",fontSize:10,color:"#4a7fa5",marginBottom:4}}>Options (comma separated)</label><input value={newField.options} onChange={e=>setNewField(f=>({...f,options:e.target.value}))} placeholder="Option 1, Option 2" style={{...inp,width:260}}/></div>}
            <button onClick={addField} style={btn("#0070CC","#fff")}>+ Add Field</button>
          </div>
          {msg&&<div style={{marginTop:10,color:msg.startsWith("✅")?"#4ADE80":"#FBB954",fontSize:12}}>{msg}</div>}
        </div>
        <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",gap:10,background:"#071428"}}>
          <button onClick={onClose} style={{...btn("transparent","#94A3B8"),border:"1px solid #1a3a5c"}}>Close</button>
          <button onClick={async()=>{setSaving(true);await onUpdate(local);setSaving(false);setMsg("✅ Saved.");}} disabled={saving} style={btn(saving?"#1a3a5c":"#4ADE80","#071428")}>{saving?"Saving...":"💾 Save & Publish"}</button>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN PANEL ──────────────────────────────────────────────
function AdminPanel({admins,currentUserEmail,onUpdate,onClose}){
  const [list,setList]=useState([...admins]);
  const [newEmail,setNewEmail]=useState("");
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState("");
  const add=()=>{
    const e=newEmail.trim().toLowerCase();
    if(!e||!e.includes("@")){setMsg("Enter a valid email");return;}
    if(list.includes(e)){setMsg("Already an admin");return;}
    setList(l=>[...l,e]);setNewEmail("");setMsg("Added — save to apply.");
  };
  const remove=(email)=>{
    if(email===currentUserEmail){setMsg("Can't remove yourself");return;}
    setList(l=>l.filter(x=>x!==email));setMsg("Removed — save to apply.");
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:520,border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.25)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)"}}>
          <div><div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>👥 Admin Management</div><div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>Admins can edit schemas, sites, and manage other admins.</div></div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          {list.map(email=>(
            <div key={email} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"#071428",border:"1px solid #1a3a5c",borderRadius:6,marginBottom:6}}>
              <div><span style={{color:"#E2E8F0",fontSize:13}}>{email}</span>{email===currentUserEmail&&<span style={{marginLeft:8,fontSize:10,color:"#7DD3FC",background:"#071428",border:"1px solid #1a3a5c",padding:"2px 6px",borderRadius:4}}>You</span>}</div>
              {email!==currentUserEmail&&<button onClick={()=>remove(email)} style={{background:"none",border:"1px solid #450A0A",color:"#F87171",borderRadius:4,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>Remove</button>}
            </div>
          ))}
          <div style={{display:"flex",gap:8,margin:"16px 0"}}>
            <input value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="teammate@company.com" onKeyDown={e=>e.key==="Enter"&&add()} style={{...inp,flex:1}}/>
            <button onClick={add} style={btn("#0070CC","#fff")}>Add</button>
          </div>
          {msg&&<div style={{color:msg.startsWith("✅")?"#4ADE80":"#FBB954",fontSize:12,marginBottom:12}}>{msg}</div>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
            <button onClick={onClose} style={{...btn("transparent","#94A3B8"),border:"1px solid #1a3a5c"}}>Close</button>
            <button onClick={async()=>{setSaving(true);await onUpdate(list);setSaving(false);setMsg("✅ Saved.");}} disabled={saving} style={btn(saving?"#1a3a5c":"#4ADE80","#071428")}>{saving?"Saving...":"💾 Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN HISTORY ─────────────────────────────────────────────
function AdminHistory({onClose}){
  const [logs,setLogs]=useState([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{
    const q=query(collection(db,"_adminHistory"),orderBy("timestamp","desc"));
    return onSnapshot(q,snap=>{setLogs(snap.docs.map(d=>({id:d.id,...d.data()})));setLoading(false);});
  },[]);
  const ACTION_COLORS={"Schema":"#38BDF8","Site":"#4ADE80","Admin":"#F87171","DefaultView":"#C084FC"};
  const getColor=(action)=>{const m=Object.keys(ACTION_COLORS).find(k=>action?.includes(k));return m?ACTION_COLORS[m]:"#94A3B8";};
  const formatTime=(ts)=>{if(!ts)return"—";const d=ts.toDate?ts.toDate():new Date(ts);return d.toLocaleString();};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:700,maxHeight:"88vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)",borderRadius:"12px 12px 0 0"}}>
          <div><div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>📋 Admin Change History</div><div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>All schema, site, admin, and view changes</div></div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1}}>
          {loading && <div style={{textAlign:"center",padding:40,color:"#1a5a8a"}}>Loading history...</div>}
          {!loading && logs.length===0 && <div style={{textAlign:"center",padding:40,color:"#1a5a8a",fontStyle:"italic"}}>No changes recorded yet</div>}
          {logs.map((log,i)=>(
            <div key={log.id} style={{padding:"14px 24px",borderBottom:"1px solid #0a1f3d",display:"flex",gap:14,alignItems:"flex-start",background:i%2===0?"#050e1f":"#071428"}}>
              <div style={{minWidth:8,height:8,borderRadius:"50%",background:getColor(log.action),marginTop:5,flexShrink:0,boxShadow:`0 0 6px ${getColor(log.action)}`}}/>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <span style={{color:getColor(log.action),fontWeight:700,fontSize:12}}>{log.action}</span>
                  <span style={{color:"#1a5a8a",fontSize:11}}>{formatTime(log.timestamp)}</span>
                </div>
                <div style={{color:"#CBD5E1",fontSize:12,marginBottom:3}}>{log.details}</div>
                <div style={{color:"#475569",fontSize:11}}>by {log.userName||log.userEmail}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",background:"#071428",borderRadius:"0 0 12px 12px"}}>
          <button onClick={onClose} style={{padding:"8px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── CHANGELOG MODAL ───────────────────────────────────────────
function ChangelogModal({onClose}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:600,maxHeight:"85vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)",borderRadius:"12px 12px 0 0"}}>
          <div><div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>📋 Changelog</div><div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>DCOPS SRS · Version history</div></div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1,padding:"16px 24px"}}>
          {CHANGELOG.map((entry,i)=>(
            <div key={entry.version} style={{marginBottom:16,paddingBottom:16,borderBottom:i<CHANGELOG.length-1?"1px solid #0a1f3d":"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                <span style={{background:i===0?"#0070CC":"#1a3a5c",color:i===0?"#fff":"#4a7fa5",borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700}}>v{entry.version}</span>
                <span style={{color:"#1a5a8a",fontSize:11}}>{entry.date}</span>
                {i===0&&<span style={{background:"#4ADE8020",color:"#4ADE80",borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700}}>CURRENT</span>}
              </div>
              <div style={{color:"#94A3B8",fontSize:12,lineHeight:1.6}}>{entry.notes}</div>
            </div>
          ))}
        </div>
        <div style={{padding:"12px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",background:"#071428",borderRadius:"0 0 12px 12px"}}>
          <button onClick={onClose} style={{padding:"7px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── MY VIEW PANEL ─────────────────────────────────────────────
function MyViewPanel({schemas,sites,isAdmin,userPrefs,defaultPrefs,onSaveUser,onSaveDefault,onClose}){
  const TAB_ORDER=["inbound","outbound","receiving","storage","transfers"];
  const [prefs,setPrefs]=useState(JSON.parse(JSON.stringify(userPrefs||{})));
  const [activeTab,setActiveTab]=useState("inbound");
  const [saving,setSaving]=useState(false);
  const [savingDefault,setSavingDefault]=useState(false);
  const [msg,setMsg]=useState("");
  const activeSites=sites.filter(s=>s.active);
  const schema=schemas[activeTab];

  const getHiddenCols=(tab)=>prefs[tab]?.hiddenCols||defaultPrefs?.[tab]?.hiddenCols||[];
  const setHiddenCols=(tab,cols)=>setPrefs(p=>({...p,[tab]:{...p[tab],hiddenCols:cols}}));
  const toggleCol=(tab,key)=>{const hidden=getHiddenCols(tab);setHiddenCols(tab,hidden.includes(key)?hidden.filter(k=>k!==key):[...hidden,key]);};
  const tableFields=schema.fields.filter(f=>f.showInTable);

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:680,maxHeight:"92vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)",borderRadius:"12px 12px 0 0"}}>
          <div><div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>👤 My View</div><div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>Personalize your portal experience. Your settings only.</div></div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{overflowY:"auto",flex:1,padding:"20px 24px"}}>
          <div style={{marginBottom:24}}>
            <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>General</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div>
                <label style={{display:"block",fontSize:11,color:"#64748B",marginBottom:5}}>Default Tab on Login</label>
                <select value={prefs.defaultTab||""} onChange={e=>setPrefs(p=>({...p,defaultTab:e.target.value}))} style={{...inp}}>
                  <option value="">— Use admin default —</option>
                  {TAB_ORDER.map(k=><option key={k} value={k}>{schemas[k]?.icon} {schemas[k]?.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,color:"#64748B",marginBottom:5}}>Default Site Filter</label>
                <select value={prefs.defaultSite||""} onChange={e=>setPrefs(p=>({...p,defaultSite:e.target.value}))} style={{...inp}}>
                  <option value="">— All Sites —</option>
                  {activeSites.map(s=><option key={s.code} value={s.code}>{s.code} — {s.city}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,color:"#64748B",marginBottom:5}}>Row Density</label>
                <select value={prefs.density||"comfortable"} onChange={e=>setPrefs(p=>({...p,density:e.target.value}))} style={{...inp}}>
                  <option value="comfortable">Comfortable</option>
                  <option value="compact">Compact (more rows)</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <div style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Column Visibility (per tab)</div>
            <div style={{display:"flex",gap:2,marginBottom:14}}>
              {TAB_ORDER.filter(k=>schemas[k]).map(k=>{
                const s=schemas[k]; const hiddenCount=getHiddenCols(k).length;
                return(
                  <button key={k} onClick={()=>setActiveTab(k)} style={{padding:"8px 14px",border:"none",cursor:"pointer",borderRadius:"6px 6px 0 0",background:activeTab===k?"#071428":"transparent",color:activeTab===k?s.color:"#475569",fontWeight:activeTab===k?700:500,fontSize:12,borderBottom:activeTab===k?`2px solid ${s.color}`:"2px solid transparent",position:"relative"}}>
                    {s.icon} {s.label}
                    {hiddenCount>0&&<span style={{marginLeft:4,fontSize:9,color:"#FBB954",background:"#1a2a0a",padding:"1px 4px",borderRadius:3}}>{hiddenCount} hidden</span>}
                  </button>
                );
              })}
            </div>
            <div style={{border:"1px solid #1a3a5c",borderRadius:8,overflow:"hidden"}}>
              <div style={{background:"#071428",padding:"8px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:"#4a7fa5",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>Table Columns — {schema.label}</span>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setHiddenCols(activeTab,[])} style={{background:"none",border:"none",color:"#4ADE80",cursor:"pointer",fontSize:11}}>Show all</button>
                  <button onClick={()=>setHiddenCols(activeTab,tableFields.map(f=>f.key))} style={{background:"none",border:"none",color:"#F87171",cursor:"pointer",fontSize:11}}>Hide all</button>
                </div>
              </div>
              {tableFields.map((f,i)=>{
                const hidden=getHiddenCols(activeTab).includes(f.key);
                return(
                  <div key={f.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderTop:i>0?"1px solid #0a1f3d":"",background:i%2===0?"#050e1f":"#071428",cursor:"pointer"}} onClick={()=>toggleCol(activeTab,f.key)}>
                    <span style={{color:hidden?"#334155":"#CBD5E1",fontSize:13,textDecoration:hidden?"line-through":"none"}}>{f.label}</span>
                    <div style={{width:40,height:22,borderRadius:11,background:hidden?"#1a3a5c":"#0070CC",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                      <div style={{position:"absolute",top:3,left:hidden?3:21,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{color:"#334155",fontSize:11,marginTop:8}}>These are your personal column preferences — other users are not affected.</div>
          </div>
          {isAdmin&&(
            <div style={{marginTop:20,padding:"14px 16px",background:"#071428",border:"1px solid #1a3a5c",borderRadius:8}}>
              <div style={{color:"#FBB954",fontSize:12,fontWeight:700,marginBottom:6}}>⚙️ Admin: Set as Default for All Users</div>
              <div style={{color:"#475569",fontSize:11,marginBottom:10}}>Save your current settings above as the default view for all new users (existing personalized settings won't be affected).</div>
              <button onClick={async()=>{setSavingDefault(true);await onSaveDefault(prefs);setSavingDefault(false);setMsg("✅ Default view saved for all new users.");}}
                disabled={savingDefault} style={{padding:"7px 16px",borderRadius:6,border:"none",background:savingDefault?"#1a3a5c":"#FBB954",color:"#071428",fontWeight:700,cursor:"pointer",fontSize:12}}>
                {savingDefault?"Saving...":"💾 Set as Team Default"}
              </button>
            </div>
          )}
          {msg&&<div style={{marginTop:12,color:"#4ADE80",fontSize:12}}>{msg}</div>}
        </div>
        <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#071428",borderRadius:"0 0 12px 12px"}}>
          <button onClick={()=>{setPrefs({});setMsg("Preferences reset to team defaults.");}} style={{padding:"7px 14px",borderRadius:6,border:"1px solid #450A0A",background:"transparent",color:"#F87171",cursor:"pointer",fontSize:12}}>Reset to Defaults</button>
          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose} style={{padding:"8px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Cancel</button>
            <button onClick={async()=>{setSaving(true);await onSaveUser(prefs);setSaving(false);setMsg("✅ Your preferences saved.");}} disabled={saving} style={{padding:"8px 24px",borderRadius:6,border:"none",background:saving?"#1a3a5c":"#0070CC",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:13}}>{saving?"Saving...":"💾 Save My View"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── DELETED ENTRIES LOG ───────────────────────────────────────
function DeletedLog({onClose}){
  const [logs,setLogs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [search,setSearch]=useState("");

  useEffect(()=>{
    const q=query(collection(db,"_deletedEntries"),orderBy("deletedAt","desc"));
    const unsub=onSnapshot(q,
      snap=>{setLogs(snap.docs.map(d=>({id:d.id,...d.data()})));setLoading(false);setError(null);},
      err=>{console.error("DeletedLog error:",err);setError(err.message||"Permission denied");setLoading(false);}
    );
    return unsub;
  },[]);

  const formatTime=(ts)=>{if(!ts)return"—";const d=ts.toDate?ts.toDate():new Date(ts);return d.toLocaleString();};

  const filtered = logs.filter(l=>{
    if(!search) return true;
    const q=search.toLowerCase();
    return JSON.stringify(l).toLowerCase().includes(q);
  });

  const summarize=(entry)=>{
    return entry?.item || entry?.description || entry?.assetId || entry?.poNumber || "Untitled entry";
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.88)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:760,maxHeight:"88vh",display:"flex",flexDirection:"column",border:"1px solid #1a3a5c",boxShadow:"0 0 60px rgba(0,100,255,0.2)"}}>
        <div style={{padding:"16px 24px",borderBottom:"1px solid #1a3a5c",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(90deg,#071428,#0a1f3d)",borderRadius:"12px 12px 0 0"}}>
          <div><div style={{color:"#E2E8F0",fontWeight:800,fontSize:16}}>🗑 Deleted Entries Log</div><div style={{color:"#4a7fa5",fontSize:11,marginTop:2}}>Full record of every deleted entry, who deleted it, and when</div></div>
          <button onClick={onClose} style={{background:"#0a1f3d",border:"1px solid #1a3a5c",color:"#94A3B8",width:32,height:32,borderRadius:6,fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{padding:"14px 24px 0"}}>
          <input placeholder="🔍  Search deleted entries..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #1a3a5c",background:"#071428",color:"#E2E8F0",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{overflowY:"auto",flex:1,padding:"14px 24px"}}>
          {loading && <div style={{textAlign:"center",padding:40,color:"#1a5a8a"}}>Loading...</div>}
          {error && (
            <div style={{textAlign:"center",padding:40,color:"#F87171"}}>
              <div style={{fontWeight:700,marginBottom:6}}>⚠️ Couldn't load the deleted entries log</div>
              <div style={{fontSize:12,color:"#94A3B8"}}>This usually means the Firestore security rules don't yet allow access to the "_deletedEntries" collection. Add a rule for it (same pattern as "_adminHistory") and republish.</div>
            </div>
          )}
          {!loading && !error && filtered.length===0 && <div style={{textAlign:"center",padding:40,color:"#1a5a8a",fontStyle:"italic"}}>No deleted entries recorded yet</div>}
          {filtered.map((log,i)=>(
            <div key={log.id} style={{padding:"12px 16px",marginBottom:8,background:i%2===0?"#071428":"#050e1f",border:"1px solid #1a3a5c",borderRadius:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div>
                  <span style={{color:"#F87171",fontWeight:700,fontSize:12}}>{log.sourceTab}</span>
                  <span style={{color:"#E2E8F0",fontSize:13,marginLeft:10}}>{summarize(log.entryData)}</span>
                </div>
                <span style={{color:"#1a5a8a",fontSize:11,whiteSpace:"nowrap"}}>{formatTime(log.deletedAt)}</span>
              </div>
              <div style={{color:"#475569",fontSize:11}}>Deleted by <strong style={{color:"#7DD3FC"}}>{log.deletedByName||log.deletedBy}</strong></div>
              {log.entryData && (
                <details style={{marginTop:8}}>
                  <summary style={{color:"#4a7fa5",fontSize:11,cursor:"pointer"}}>View entry details</summary>
                  <div style={{marginTop:8,padding:"8px 10px",background:"#020912",borderRadius:6,fontSize:11,color:"#94A3B8",display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                    {Object.entries(log.entryData).filter(([k])=>!k.startsWith("_")&&!["id","createdAt","lastEditedAt"].includes(k)).map(([k,v])=>(
                      <div key={k}><span style={{color:"#334155"}}>{k}:</span> {String(v)}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
        <div style={{padding:"12px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",background:"#071428",borderRadius:"0 0 12px 12px"}}>
          <button onClick={onClose} style={{padding:"7px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── SHEET VIEW ───────────────────────────────────────────────
function SheetView({tabId,schema,schemas,user,sites,hiddenCols,density,defaultSite,isAdmin}){
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null);
  const [moveEntry,setMoveEntry]=useState(null);
  const [search,setSearch]=useState("");
  const [siteFilter,setSite]=useState(defaultSite||"All");
  const [statusFilter,setStatus]=useState("All");
  const [deleting,setDeleting]=useState(null);
  const [selected,setSelected]=useState(new Set());
  const [sortCol,setSortCol]=useState(null);
  const [sortDir,setSortDir]=useState("asc");
  const [page,setPage]=useState(1);
  const [pageSize,setPageSize]=useState(20);
  const [showMoved,setShowMoved]=useState(true);
  const [confirmDelete,setConfirmDelete]=useState(null);
  const activeSites=sites.filter(s=>s.active);

  const handleSort=(colKey)=>{
    if(sortCol===colKey) setSortDir(d=>d==="asc"?"desc":"asc");
    else{setSortCol(colKey);setSortDir("asc");}
    setPage(1);
  };

  useEffect(()=>{
    const q=query(collection(db,tabId),orderBy("createdAt","desc"));
    return onSnapshot(q,snap=>{setRows(snap.docs.map(d=>({id:d.id,...d.data()})));setLoading(false);});
  },[tabId]);

  const tableFields=schema.fields.filter(f=>f.showInTable && !(hiddenCols||[]).includes(f.key));

  const filtered=useMemo(()=>{
    let result=rows.filter(r=>{
      if(!showMoved && r._locked) return false;
      const q=search.toLowerCase();
      const ms=!q||Object.values(r).some(v=>String(v).toLowerCase().includes(q));
      const sv=r.site||r.fromSite||"";
      const msite=siteFilter==="All"||sv===siteFilter;
      const stv=r[schema.statusField]||"";
      const mst=statusFilter==="All"||stv===statusFilter;
      return ms&&msite&&mst;
    });
    if(sortCol){
      result=[...result].sort((a,b)=>{
        const av=String(a[sortCol]||"").toLowerCase();
        const bv=String(b[sortCol]||"").toLowerCase();
        return sortDir==="asc"?av.localeCompare(bv):bv.localeCompare(av);
      });
    }
    return result;
  },[rows,search,siteFilter,statusFilter,schema.statusField,sortCol,sortDir,showMoved]);

  const movedCount = rows.filter(r=>r._locked).length;

  const toggleSelect=(id)=>setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});
  const clearAll=()=>setSelected(new Set());
  const selectAll=()=>setSelected(new Set(filtered.map(r=>r.id)));
  const allSelected=filtered.length>0&&filtered.every(r=>selected.has(r.id));
  const someSelected=selected.size>0;

  const totalPages = pageSize==="all" ? 1 : Math.ceil(filtered.length/Number(pageSize));
  const pagedRows = pageSize==="all" ? filtered : filtered.slice((page-1)*Number(pageSize), page*Number(pageSize));

  const save=async(form)=>{
    const payload={...form,lastEditedBy:user?.displayName||user?.email||"unknown",lastEditedAt:serverTimestamp()};
    if(form.id){
      const {id,createdAt,createdBy,...rest}=payload;
      await updateDoc(doc(db,tabId,form.id),rest);
    } else {
      await addDoc(collection(db,tabId),{...payload,createdBy:user?.displayName||user?.email||"unknown",createdAt:serverTimestamp()});
    }
    setModal(null);
  };

  const onMove = async(entry, targetTab, keepOriginal) => {
    const {id, createdAt, createdBy, lastEditedAt, lastEditedBy, status, condition, ...rest} = entry;
    const now = new Date().toISOString();
    const todayStr = now.slice(0,10);
    const mover = user?.displayName || user?.email || "unknown";
    const historyEntry = { from: tabId, to: targetTab, by: mover, at: now };
    const existingHistory = entry._moveHistory || [];
    const newHistory = [...existingHistory, historyEntry];

    // Auto-update the relevant date field for the destination tab
    const dateField = DATE_FIELD_ON_MOVE[targetTab];
    const dateUpdate = dateField ? { [dateField]: todayStr } : {};

    const newEntry = {
      ...rest,
      ...dateUpdate,
      movedFrom: tabId,
      movedFromId: id,
      _moveHistory: newHistory,
      createdBy: entry.createdBy || mover,
      createdAt: serverTimestamp(),
      lastEditedBy: mover,
      lastEditedAt: serverTimestamp(),
    };
    await addDoc(collection(db, targetTab), newEntry);
    if(keepOriginal){
      await updateDoc(doc(db, tabId, id), {
        status: "Moved",
        movedTo: targetTab,
        _moveHistory: newHistory,
        _locked: true,
        lastEditedBy: mover,
        lastEditedAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(doc(db, tabId, id));
    }
    setMoveEntry(null);
  };

  const performDelete = async(entry) => {
    const mover = user?.displayName || user?.email || "unknown";
    try {
      await addDoc(collection(db, "_deletedEntries"), {
        sourceTab: tabId,
        entryData: entry,
        deletedBy: user?.email || "unknown",
        deletedByName: mover,
        deletedAt: serverTimestamp(),
      });
    } catch(e) { console.error("Delete log error:", e); }
    await deleteDoc(doc(db, tabId, entry.id));
    setConfirmDelete(null);
    setModal(null);
  };

  const exportToExcel = (rowsToExport) => {
    const source = rowsToExport && rowsToExport.length > 0 ? rowsToExport : filtered;
    if(!source || source.length === 0) return;
    const exportRows = source.map(row => {
      const out = {};
      schema.fields.forEach(f => { out[f.label] = row[f.key] !== undefined ? row[f.key] : ""; });
      out["Created By"] = row.createdBy || "";
      out["Last Edited By"] = row.lastEditedBy || "";
      if(row.movedTo) out["Moved To"] = row.movedTo;
      return out;
    });
    try {
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const cols = Object.keys(exportRows[0] || {});
      ws["!cols"] = cols.map(k => ({ wch: Math.max(k.length, 16) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, schema.label.slice(0,31));
      const date = new Date().toISOString().slice(0,10);
      XLSX.writeFile(wb, `DCOPS_${schema.label}_${date}.xlsx`, {bookType:"xlsx", type:"binary"});
    } catch(e) {
      const headers = Object.keys(exportRows[0]);
      const csv = [headers.join(","), ...exportRows.map(r => headers.map(h => JSON.stringify(r[h]||"")).join(","))].join("\n");
      const blob = new Blob([csv], {type:"text/csv"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DCOPS_${schema.label}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return(
    <div>
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <input placeholder="🔍  Search entries..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
          style={{flex:1,minWidth:180,padding:"8px 12px",borderRadius:6,border:"1px solid #1a3a5c",background:"#071428",color:"#E2E8F0",fontSize:13,outline:"none"}}/>
        <select value={siteFilter} onChange={e=>{setSite(e.target.value);setPage(1);}} style={{padding:"8px 10px",borderRadius:6,border:"1px solid #1a3a5c",background:"#071428",color:"#E2E8F0",fontSize:13}}>
          <option value="All">All Sites</option>
          {activeSites.map(s=><option key={s.code} value={s.code}>{s.code} — {s.city}</option>)}
        </select>
        <select value={statusFilter} onChange={e=>{setStatus(e.target.value);setPage(1);}} style={{padding:"8px 10px",borderRadius:6,border:"1px solid #1a3a5c",background:"#071428",color:"#E2E8F0",fontSize:13}}>
          <option value="All">All Statuses</option>
          {schema.statusOptions.map(s=><option key={s}>{s}</option>)}
        </select>
        <button onClick={()=>setModal("new")} style={{padding:"8px 18px",borderRadius:6,border:"none",background:schema.color,color:"#050e1f",fontWeight:700,cursor:"pointer",fontSize:13,whiteSpace:"nowrap"}}>+ Add Entry</button>
        {someSelected && (
          <button onClick={()=>exportToExcel(filtered.filter(r=>selected.has(r.id)))}
            style={{padding:"8px 14px",borderRadius:6,border:"1px solid #4ADE80",background:"#4ADE8018",color:"#4ADE80",cursor:"pointer",fontSize:13,whiteSpace:"nowrap",fontWeight:700}}>
            ⬇ Export Selected ({selected.size})
          </button>
        )}
        <button onClick={()=>exportToExcel(filtered)} disabled={filtered.length===0}
          style={{padding:"8px 14px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:filtered.length===0?"#1a3a5c":"#94A3B8",cursor:filtered.length===0?"default":"pointer",fontSize:13,whiteSpace:"nowrap"}}>
          ⬇ Export All
        </button>
      </div>

      <div style={{fontSize:11,color:"#1a5a8a",marginBottom:8,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span>{loading?"Loading...":`Showing ${pageSize==="all"?filtered.length:Math.min(Number(pageSize),filtered.length)} of ${filtered.length} entries (${rows.length} total) · Live sync`}</span>
        {someSelected&&(
          <span style={{color:"#7DD3FC"}}>
            {selected.size} selected
            <button onClick={clearAll} style={{marginLeft:8,background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:11,textDecoration:"underline"}}>clear</button>
          </span>
        )}
        {movedCount>0 && (
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",color:"#4a7fa5"}}>
            <input type="checkbox" checked={showMoved} onChange={e=>setShowMoved(e.target.checked)} style={{accentColor:"#4B9FE1",width:13,height:13,cursor:"pointer"}}/>
            Show moved entries ({movedCount})
          </label>
        )}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          <span style={{color:"#334155",fontSize:11}}>Show:</span>
          {["20","50","100","all"].map(s=>(
            <button key={s} onClick={()=>{setPageSize(s);setPage(1);}} style={{
              padding:"2px 8px",borderRadius:4,border:`1px solid ${pageSize===s?"#4B9FE1":"#1a3a5c"}`,
              background:pageSize===s?"#4B9FE1":"transparent",
              color:pageSize===s?"#020912":"#4a7fa5",cursor:"pointer",fontSize:11,fontWeight:pageSize===s?700:400
            }}>{s}</button>
          ))}
        </div>
      </div>

      <div style={{overflowX:"auto",borderRadius:8,border:"1px solid #1a3a5c"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:900}}>
          <thead><tr style={{background:"#071428"}}>
            <th style={{padding:"10px 12px",borderBottom:"1px solid #1a3a5c",width:36}} onClick={e=>e.stopPropagation()}>
              <input type="checkbox" checked={allSelected} onChange={e=>{e.stopPropagation();e.target.checked?selectAll():clearAll();}}
                onClick={e=>e.stopPropagation()}
                style={{accentColor:"#0070CC",width:15,height:15,cursor:"pointer"}} title={allSelected?"Deselect all":"Select all"}/>
            </th>
            {tableFields.map(f=>(
              <th key={f.key} onClick={()=>handleSort(f.key)}
                style={{padding:"10px 12px",color:sortCol===f.key?"#7DD3FC":"#4a7fa5",fontWeight:700,textAlign:"left",whiteSpace:"nowrap",fontSize:10,letterSpacing:0.8,textTransform:"uppercase",borderBottom:"1px solid #1a3a5c",cursor:"pointer",userSelect:"none"}}>
                {f.label}
                <span style={{marginLeft:4,opacity:sortCol===f.key?1:0.3}}>{sortCol===f.key?(sortDir==="asc"?"▲":"▼"):"⇅"}</span>
              </th>
            ))}
            <th style={{padding:"10px 12px",color:"#4a7fa5",fontWeight:700,fontSize:10,borderBottom:"1px solid #1a3a5c"}}>By</th>
            <th style={{padding:"10px 12px",color:"#4a7fa5",fontWeight:700,fontSize:10,borderBottom:"1px solid #1a3a5c"}}>Actions</th>
          </tr></thead>
          <tbody>
            {loading&&<tr><td colSpan={tableFields.length+2} style={{textAlign:"center",padding:40,color:"#1a5a8a"}}>Loading...</td></tr>}
            {!loading&&pagedRows.length===0&&<tr><td colSpan={tableFields.length+2} style={{textAlign:"center",padding:40,color:"#1a5a8a",fontStyle:"italic"}}>No entries yet — click "+ Add Entry"</td></tr>}
            {pagedRows.map((row,i)=>{
              const locked = !!row._locked;
              return(
              <tr key={row.id} style={{background:locked?"#0a0f1a":i%2===0?"#050e1f":"#071428",borderBottom:"1px solid #1a3a5c",opacity:locked?0.6:1,cursor:"pointer"}}
                onClick={()=>setModal(row)}
                onMouseEnter={e=>e.currentTarget.style.background=locked?"#0d1420":"#0a1f3d"}
                onMouseLeave={e=>e.currentTarget.style.background=locked?"#0a0f1a":i%2===0?"#050e1f":"#071428"}>
                <td style={{padding:density==="compact"?"5px 12px":"9px 12px"}} onClick={e=>e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(row.id)} disabled={locked}
                    onChange={()=>toggleSelect(row.id)} onClick={e=>e.stopPropagation()}
                    style={{accentColor:"#0070CC",width:15,height:15,cursor:locked?"not-allowed":"pointer"}}/>
                </td>
                {tableFields.map(f=>(
                  <td key={f.key} style={{padding:density==="compact"?"5px 12px":"9px 12px",color:"#CBD5E1",maxWidth:180}}>
                    {f.key===schema.statusField
                      ? <>{locked&&row.movedTo&&<span style={{fontSize:10,color:"#4a7fa5",marginRight:4}}>🔒→{row.movedTo}</span>}<StatusBadge value={row[f.key]}/></>
                      :f.type==="site"?<SiteBadge code={row[f.key]} sites={sites}/>
                      :f.type==="checkbox"?<span>{row[f.key]?"✓":"—"}</span>
                      :<span style={{display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160}}>{row[f.key]||<span style={{color:"#1a3a5c"}}>—</span>}</span>}
                  </td>
                ))}
                <td style={{padding:density==="compact"?"5px 12px":"9px 12px",color:"#1a5a8a",fontSize:11}}>{row.createdBy?.split(" ")[0]||"—"}</td>
                <td style={{padding:density==="compact"?"3px 12px":"6px 12px",whiteSpace:"nowrap"}} onClick={e=>e.stopPropagation()}>
                  {locked ? (
                    <>
                      <button onClick={()=>setModal(row)} style={{padding:"4px 10px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11,marginRight:4}}>👁 View</button>
                      {isAdmin && (
                        <button onClick={()=>setConfirmDelete(row)} style={{padding:"4px 10px",borderRadius:4,border:"1px solid #450A0A",background:"transparent",color:"#F87171",cursor:"pointer",fontSize:11}}>🗑 Admin Del</button>
                      )}
                    </>
                  ) : (
                    <>
                      <button onClick={()=>setModal(row)} style={{padding:"4px 10px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11,marginRight:4}}>Edit</button>
                      <button onClick={()=>setMoveEntry(row)} style={{padding:"4px 10px",borderRadius:4,border:"1px solid #1a3a6c",background:"transparent",color:"#7DD3FC",cursor:"pointer",fontSize:11,marginRight:4}}>Move</button>
                      <button onClick={()=>setConfirmDelete(row)}
                        style={{padding:"4px 10px",borderRadius:4,border:"1px solid #450A0A",background:"transparent",color:"#F87171",cursor:"pointer",fontSize:11}}>
                        Del
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {pageSize!=="all" && totalPages>1 && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:12}}>
          <button onClick={()=>setPage(1)} disabled={page===1} style={{padding:"4px 8px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:page===1?"#1a3a5c":"#4a7fa5",cursor:page===1?"default":"pointer",fontSize:11}}>«</button>
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:"4px 8px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:page===1?"#1a3a5c":"#4a7fa5",cursor:page===1?"default":"pointer",fontSize:11}}>‹</button>
          {Array.from({length:Math.min(7,totalPages)},(_,i)=>{
            let p;
            if(totalPages<=7) p=i+1;
            else if(page<=4) p=i+1;
            else if(page>=totalPages-3) p=totalPages-6+i;
            else p=page-3+i;
            return <button key={p} onClick={()=>setPage(p)} style={{padding:"4px 10px",borderRadius:4,border:`1px solid ${page===p?"#4B9FE1":"#1a3a5c"}`,background:page===p?"#4B9FE1":"transparent",color:page===p?"#020912":"#4a7fa5",cursor:"pointer",fontSize:11,fontWeight:page===p?700:400}}>{p}</button>;
          })}
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:"4px 8px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:page===totalPages?"#1a3a5c":"#4a7fa5",cursor:page===totalPages?"default":"pointer",fontSize:11}}>›</button>
          <button onClick={()=>setPage(totalPages)} disabled={page===totalPages} style={{padding:"4px 8px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:page===totalPages?"#1a3a5c":"#4a7fa5",cursor:page===totalPages?"default":"pointer",fontSize:11}}>»</button>
          <span style={{color:"#334155",fontSize:11,marginLeft:4}}>Page {page} of {totalPages}</span>
        </div>
      )}

      {modal&&<EntryModal schema={schema} item={modal==="new"?null:modal} user={user} sites={sites} isAdmin={isAdmin}
        readOnly={modal!=="new" && !!modal._locked} onSave={save} onClose={()=>setModal(null)}
        onMoveClick={(entry)=>{setModal(null);setMoveEntry(entry);}}
        onDeleteClick={(entry)=>setConfirmDelete(entry)}/>}
      {moveEntry&&<MoveModal entry={moveEntry} currentTab={tabId} schemas={schemas} onMove={onMove} onClose={()=>setMoveEntry(null)}/>}
      {confirmDelete&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,5,20,0.92)",zIndex:1200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"#050e1f",borderRadius:12,width:"100%",maxWidth:440,border:"1px solid #450A0A",boxShadow:"0 0 60px rgba(255,0,0,0.15)"}}>
            <div style={{padding:"20px 24px"}}>
              <div style={{color:"#F87171",fontWeight:800,fontSize:16,marginBottom:8}}>⚠️ Delete this entry?</div>
              <div style={{color:"#94A3B8",fontSize:13,marginBottom:4}}>{confirmDelete.item || confirmDelete.description || confirmDelete.assetId || "This entry"} will be permanently removed from {schema.label}.</div>
              <div style={{color:"#475569",fontSize:12,marginTop:8}}>This action is logged in the Deleted Entries log and cannot be undone.</div>
            </div>
            <div style={{padding:"14px 24px",borderTop:"1px solid #1a3a5c",display:"flex",justifyContent:"flex-end",gap:10,background:"#071428",borderRadius:"0 0 12px 12px"}}>
              <button onClick={()=>setConfirmDelete(null)} style={{padding:"8px 20px",borderRadius:6,border:"1px solid #1a3a5c",background:"transparent",color:"#94A3B8",cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>performDelete(confirmDelete)} style={{padding:"8px 24px",borderRadius:6,border:"none",background:"#DC2626",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:13}}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── LOGIN ────────────────────────────────────────────────────
function LoginScreen({onLogin,loading}){
  return(
    <div style={{minHeight:"100vh",background:"#020912",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans','Segoe UI',sans-serif",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
        {[["▲","#00439C","-10%","15%","180px","0.04"],["●","#E31E52","80%","-5%","220px","0.04"],["■","#0070CC","75%","70%","160px","0.04"],["✕","#00A651","5%","75%","200px","0.04"]].map(([sym,color,left,top,size,op],i)=>(
          <div key={i} style={{position:"absolute",left,top,fontSize:size,color,opacity:op,fontWeight:900,lineHeight:1,userSelect:"none"}}>{sym}</div>
        ))}
      </div>
      <div style={{textAlign:"center",maxWidth:440,padding:40,position:"relative",zIndex:1}}>
        <div style={{margin:"0 auto 24px",width:80,height:80,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <DCopsLogo size={80}/>
        </div>
        <h1 style={{color:"#E2E8F0",fontSize:28,fontWeight:800,margin:"0 0 8px",letterSpacing:-0.5}}>DCOPS SRS</h1>
        <p style={{color:"#4a7fa5",fontSize:13,margin:"8px 0 36px"}}>Data Center Logistics Portal<br/><span style={{fontSize:11,color:"#1a5a8a"}}>Shipping · Receiving · Storage</span></p>
        <button onClick={onLogin} disabled={loading} style={{display:"flex",alignItems:"center",gap:12,margin:"0 auto",padding:"14px 32px",borderRadius:10,border:"1px solid #1a3a5c",background:"#071428",color:"#E2E8F0",fontSize:14,fontWeight:600,cursor:loading?"default":"pointer",boxShadow:"0 0 30px rgba(0,112,204,0.2)"}}>
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading?"Signing in...":"Sign in with Google"}
        </button>
        <p style={{color:"#1a3a5c",fontSize:11,marginTop:20}}>Sign in with your work Google account</p>
      </div>
    </div>
  );
}

// ── MAIN APP ─────────────────────────────────────────────────
export default function App(){
  const [user,setUser]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  const [loginLoading,setLoginLoading]=useState(false);
  const [activeTab,setActiveTab]=useState("inbound");
  const [schemas,setSchemas]=useState(DEFAULT_SCHEMAS);
  const [sites,setSites]=useState(DEFAULT_SITES);
  const [admins,setAdmins]=useState([]);
  const [userPrefs,setUserPrefs]=useState({});
  const [defaultPrefs,setDefaultPrefs]=useState({});
  const [showSchema,setShowSchema]=useState(false);
  const [showSites,setShowSites]=useState(false);
  const [showAdmins,setShowAdmins]=useState(false);
  const [showHistory,setShowHistory]=useState(false);
  const [showMyView,setShowMyView]=useState(false);
  const [showChangelog,setShowChangelog]=useState(false);
  const [showDeletedLog,setShowDeletedLog]=useState(false);

  useEffect(()=>onAuthStateChanged(auth,u=>{setUser(u);setAuthLoading(false);}),[]);

  useEffect(()=>{
    const u1=onSnapshot(doc(db,"_config","schemas"),s=>{if(s.exists())setSchemas(s.data());});
    const u2=onSnapshot(doc(db,"_config","sites"),s=>{if(s.exists())setSites(s.data().list||DEFAULT_SITES);});
    const u3=onSnapshot(doc(db,"_config","admins"),s=>{if(s.exists())setAdmins(s.data().list||[]);});
    const u4=onSnapshot(doc(db,"_config","defaultPrefs"),s=>{if(s.exists())setDefaultPrefs(s.data());});
    return ()=>{u1();u2();u3();u4();};
  },[]);

  useEffect(()=>{
    if(!user) return;
    const unsub=onSnapshot(doc(db,"_userPrefs",user.uid),s=>{if(s.exists())setUserPrefs(s.data());});
    return unsub;
  },[user]);

  const isAdmin=user&&(admins.includes(user.email)||admins.length===0);

  useEffect(()=>{
    if(user && admins.length===0){
      const timer = setTimeout(()=>{
        setAdmins(prev=>{
          if(prev.length===0){
            setDoc(doc(db,"_config","admins"),{list:[user.email]});
            return [user.email];
          }
          return prev;
        });
      },1500);
      return ()=>clearTimeout(timer);
    }
  },[user]);

  const logAdminChange = async (action, details) => {
    if(!user) return;
    try {
      await addDoc(collection(db,"_adminHistory"),{
        userEmail:user.email, userName:user.displayName, action, details, timestamp:serverTimestamp(),
      });
    } catch(e){ console.error("Log error:",e); }
  };

  const saveSchemas=async(s)=>{ await logAdminChange("Schema Editor","Updated field schemas"); await setDoc(doc(db,"_config","schemas"),s); };
  const saveSites=async(l)=>{
    const prev=sites;
    const added=l.filter(s=>!prev.find(p=>p.code===s.code));
    const deactivated=l.filter(s=>!s.active&&prev.find(p=>p.code===s.code&&p.active));
    const reactivated=l.filter(s=>s.active&&prev.find(p=>p.code===s.code&&!p.active));
    if(added.length) await logAdminChange("Site Added",`Added: ${added.map(s=>s.code).join(", ")}`);
    if(deactivated.length) await logAdminChange("Site Deactivated",`Deactivated: ${deactivated.map(s=>s.code).join(", ")}`);
    if(reactivated.length) await logAdminChange("Site Reactivated",`Reactivated: ${reactivated.map(s=>s.code).join(", ")}`);
    await setDoc(doc(db,"_config","sites"),{list:l});
  };
  const saveAdmins=async(l)=>{
    const added=l.filter(e=>!admins.includes(e));
    const removed=admins.filter(e=>!l.includes(e));
    if(added.length) await logAdminChange("Admin Added",`Added admin: ${added.join(", ")}`);
    if(removed.length) await logAdminChange("Admin Removed",`Removed admin: ${removed.join(", ")}`);
    await setDoc(doc(db,"_config","admins"),{list:l});
  };
  const saveUserPrefs=async(prefs)=>{ await setDoc(doc(db,"_userPrefs",user.uid),{...prefs,updatedAt:serverTimestamp()}); setUserPrefs(prefs); };
  const saveDefaultPrefs=async(prefs)=>{
    await logAdminChange("DefaultView","Updated default view for all new users");
    await setDoc(doc(db,"_config","defaultPrefs"),{...prefs,updatedAt:serverTimestamp()});
    setDefaultPrefs(prefs);
  };

  const [exportingAll,setExportingAll]=useState(false);
  const exportAllCategories = async () => {
    setExportingAll(true);
    try {
      const TAB_ORDER_EXPORT = ["inbound","outbound","receiving","storage","transfers"];
      const wb = XLSX.utils.book_new();
      for (const tabKey of TAB_ORDER_EXPORT) {
        const sch = schemas[tabKey];
        if (!sch) continue;
        const snap = await getDocs(query(collection(db, tabKey), orderBy("createdAt","desc")));
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const exportRows = rows.map(row => {
          const out = {};
          sch.fields.forEach(f => { out[f.label] = row[f.key] !== undefined ? row[f.key] : ""; });
          out["Created By"] = row.createdBy || "";
          out["Last Edited By"] = row.lastEditedBy || "";
          if (row.movedTo) out["Moved To"] = row.movedTo;
          return out;
        });
        const ws = exportRows.length > 0
          ? XLSX.utils.json_to_sheet(exportRows)
          : XLSX.utils.aoa_to_sheet([sch.fields.map(f=>f.label)]);
        if (exportRows.length > 0) {
          const cols = Object.keys(exportRows[0]);
          ws["!cols"] = cols.map(k => ({ wch: Math.max(k.length, 16) }));
        }
        XLSX.utils.book_append_sheet(wb, ws, sch.label.slice(0,31));
      }
      const date = new Date().toISOString().slice(0,10);
      XLSX.writeFile(wb, `DCOPS_All_Categories_${date}.xlsx`, {bookType:"xlsx", type:"binary"});
    } catch(e) {
      console.error("Export all error:", e);
      alert("Export failed — check console for details.");
    }
    setExportingAll(false);
  };

  const handleLogin=async()=>{setLoginLoading(true);try{await signInWithPopup(auth,googleProvider);}catch(e){setLoginLoading(false);}};
  const handleLogout=async()=>signOut(auth);

  const TAB_ORDER=["inbound","outbound","receiving","storage","transfers"];

  const effectivePrefs = useMemo(()=>({
    defaultTab: userPrefs.defaultTab || defaultPrefs.defaultTab || "inbound",
    defaultSite: userPrefs.defaultSite || defaultPrefs.defaultSite || "",
    density: userPrefs.density || defaultPrefs.density || "comfortable",
    ...TAB_ORDER.reduce((acc,k)=>({...acc,[k]:{hiddenCols:userPrefs[k]?.hiddenCols ?? defaultPrefs[k]?.hiddenCols ?? []}}),{})
  }),[userPrefs,defaultPrefs]);

  const hasAppliedDefaultTab=useRef(false);
  useEffect(()=>{
    if(user && !hasAppliedDefaultTab.current){
      hasAppliedDefaultTab.current=true;
      const defaultTab=userPrefs.defaultTab||defaultPrefs.defaultTab;
      if(defaultTab) setActiveTab(defaultTab);
    }
  },[user,userPrefs.defaultTab,defaultPrefs.defaultTab]);

  if(authLoading) return <div style={{minHeight:"100vh",background:"#020912",display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{color:"#1a5a8a",fontFamily:"sans-serif"}}>Loading...</div></div>;
  if(!user) return <LoginScreen onLogin={handleLogin} loading={loginLoading}/>;

  const tab=schemas[activeTab];
  const tabKeys=TAB_ORDER.filter(k=>schemas[k]);

  return(
    <div style={{minHeight:"100vh",background:"#020912",fontFamily:"'DM Sans','Segoe UI',sans-serif",color:"#E2E8F0"}}>
      <div style={{background:"linear-gradient(90deg,#050e1f,#071428)",borderBottom:"1px solid #1a3a5c"}}>
        <div style={{maxWidth:1600,margin:"0 auto",padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:56}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <DCopsLogo size={34}/>
            <div>
              <span style={{color:"#E2E8F0",fontWeight:800,fontSize:15,letterSpacing:0.3}}>DCOPS SRS</span>
              <span style={{color:"#1a5a8a",fontSize:11,marginLeft:10}}>Logistics Portal</span>
              <button onClick={()=>setShowChangelog(true)} style={{marginLeft:10,padding:"2px 7px",borderRadius:4,border:"1px solid #1a3a5c",background:"transparent",color:"#334155",cursor:"pointer",fontSize:10,fontWeight:600}}>v{APP_VERSION}</button>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:"#00E676",boxShadow:"0 0 8px #00E676"}}/>
            <span style={{color:"#1a5a8a",fontSize:11}}>Live</span>
            <button onClick={exportAllCategories} disabled={exportingAll} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:exportingAll?"#334155":"#4ADE80",cursor:exportingAll?"default":"pointer",fontSize:11,whiteSpace:"nowrap"}}>
              {exportingAll ? "⏳ Exporting..." : "⬇ Export All Categories"}
            </button>
            {isAdmin&&(
              <>
                <div style={{width:1,height:20,background:"#1a3a5c",margin:"0 2px"}}/>
                <button onClick={()=>setShowSchema(true)} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11}}>⚙️ Schema</button>
                <button onClick={()=>setShowSites(true)} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11}}>🏢 Sites</button>
                <button onClick={()=>setShowAdmins(true)} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11}}>👥 Admins</button>
                <button onClick={()=>setShowHistory(true)} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11}}>📋 History</button>
                <button onClick={()=>setShowDeletedLog(true)} style={{padding:"5px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#4a7fa5",cursor:"pointer",fontSize:11}}>🗑 Deleted Log</button>
              </>
            )}
            <div style={{width:1,height:20,background:"#1a3a5c",margin:"0 2px"}}/>
            {user.photoURL&&<img src={user.photoURL} alt="" style={{width:26,height:26,borderRadius:"50%",border:"1px solid #1a3a5c"}}/>}
            <span style={{color:"#4a7fa5",fontSize:12}}>{user.displayName?.split(" ")[0]}</span>
            <button onClick={()=>setShowMyView(true)} style={{padding:"4px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#C084FC",cursor:"pointer",fontSize:11}}>👤 My View</button>
            <button onClick={handleLogout} style={{padding:"4px 10px",borderRadius:5,border:"1px solid #1a3a5c",background:"transparent",color:"#1a5a8a",cursor:"pointer",fontSize:11}}>Sign out</button>
          </div>
        </div>
        <div style={{maxWidth:1600,margin:"0 auto",padding:"0 24px",display:"flex",gap:2}}>
          {tabKeys.map(k=>{
            const s=schemas[k];
            return <button key={k} onClick={()=>setActiveTab(k)} style={{padding:"10px 18px",border:"none",cursor:"pointer",borderRadius:"6px 6px 0 0",background:activeTab===k?"#020912":"transparent",color:activeTab===k?s.color:"#1a5a8a",fontWeight:activeTab===k?700:500,fontSize:12,borderBottom:activeTab===k?`2px solid ${s.color}`:"2px solid transparent",transition:"all 0.15s"}}>{s.icon} {s.label}</button>;
          })}
        </div>
      </div>

      <div style={{maxWidth:1600,margin:"0 auto",padding:"24px"}}>
        <div style={{marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <h2 style={{margin:0,fontSize:15,fontWeight:700,color:"#E2E8F0"}}><span style={{color:tab.color}}>{tab.icon}</span> {tab.label.toUpperCase()}</h2>
          <div style={{flex:1,height:1,background:"linear-gradient(90deg,#1a3a5c,transparent)"}}/>
          <span style={{fontSize:11,color:"#1a5a8a"}}>{sites.filter(s=>s.active).length} active sites</span>
        </div>
        <div style={{background:"#050e1f",borderRadius:10,padding:20,border:"1px solid #1a3a5c",boxShadow:"0 0 40px rgba(0,50,150,0.1)"}}>
          <SheetView key={activeTab} tabId={activeTab} schema={schemas[activeTab]} schemas={schemas} user={user} sites={sites} hiddenCols={effectivePrefs[activeTab]?.hiddenCols||[]} density={effectivePrefs.density||"comfortable"} defaultSite={effectivePrefs.defaultSite||""} isAdmin={isAdmin}/>
        </div>
      </div>

      {showSchema&&<SchemaEditor schemas={schemas} onUpdate={saveSchemas} onClose={()=>setShowSchema(false)}/>}
      {showSites&&<SiteManager sites={sites} onUpdate={saveSites} onClose={()=>setShowSites(false)}/>}
      {showAdmins&&<AdminPanel admins={admins} currentUserEmail={user.email} onUpdate={saveAdmins} onClose={()=>setShowAdmins(false)}/>}
      {showHistory&&<AdminHistory onClose={()=>setShowHistory(false)}/>}
      {showChangelog&&<ChangelogModal onClose={()=>setShowChangelog(false)}/>}
      {showDeletedLog&&<DeletedLog onClose={()=>setShowDeletedLog(false)}/>}
      {showMyView&&<MyViewPanel schemas={schemas} sites={sites} isAdmin={isAdmin} userPrefs={userPrefs} defaultPrefs={defaultPrefs} onSaveUser={saveUserPrefs} onSaveDefault={saveDefaultPrefs} onClose={()=>setShowMyView(false)}/>}
    </div>
  );
}
